/**
Author - Venkatesh Pullaganti😃
Git - https://github.com/venkatesh1146
**/

import React, {useState} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {ActionButton} from 'screens/commons';
import IMAGES from 'constants/Images';
import {KYC_FLOWS} from 'constants/FlowConstants';

const propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  flow: PropTypes.string,
};

const InitialChoiceView = ({isLoading, onSubmit, flow}) => {
  const [selectedOption, setSelectedOption] = useState('verify_otp');

  const handleSubmit = () => {
    const action = selectedOption === 'verify_otp' ? 'continue' : 'skip';
    onSubmit(action);
  };

  // ALL_FI has no income-declaration fallback step - a single "Continue" CTA straight
  // into the AA consent flow, instead of the FNO OTP-vs-upload-statement choice.
  if (flow === KYC_FLOWS.ALL_FI) {
    return (
      <AllFiIntro>
        <img src={IMAGES.phone_icon} alt="Connect accounts" style={{height: '4rem'}} />
        <h3 className="body-medium">Connect your accounts</h3>
        <p className="body-small">
          Securely connect your bank, mutual fund, and investment accounts to calculate your net
          worth. Powered by the RBI-backed Account Aggregator framework.
        </p>
        <StyledActionButton
          buttonText="Continue"
          callback={() => onSubmit('continue')}
          isLoading={isLoading}
          isDisabled={isLoading}
          rightIcon={<img src={IMAGES.arrowRight} alt="Continue" style={{width: 20, height: 20}} />}
        />
      </AllFiIntro>
    );
  }

  const options = [
    {
      value: 'verify_otp',
      label: 'Verify Income via Bank OTP',
      isRecommended: true,
      icon: <img src={IMAGES.phone_icon} alt="Bank OTP" style={{height: '4rem'}} />,
      features: [
        {
          icon: <img src={IMAGES.clockIcon} alt="Clock" style={{width: 20, height: 20}} />,
          text: 'Quick and secure verification through Account Aggregator',
        },
        {
          icon: <img src={IMAGES.questionMark} alt="Info" style={{width: 20, height: 20}} />,
          text: 'You will be redirected to verify with OTP',
        },
      ],
      infoMessage: 'Fastest way to verify your income account with secure government-backed system',
      infoVariant: 'info',
    },
    {
      value: 'upload_statement',
      label: 'Verify Income without Bank OTP',
      isRecommended: false,
      icon: <img src={IMAGES.fileIcon} alt="Upload" style={{height: '3rem'}} />,
      features: [
        {
          icon: <img src={IMAGES.fileIcon} alt="File" style={{width: 20, height: 20}} />,
          text: 'Upload your income statement document',
        },
        {
          icon: <img src={IMAGES.clockIcon} alt="Clock" style={{width: 20, height: 20}} />,
          text: 'Manual verification may take longer',
        },
      ],
      infoMessage:
        'Ensure your document is clear and provide correct password if password protected',
      infoVariant: 'warning',
    },
  ];

  return (
    <>
      <OptionsList>
        {options.map((option) => (
          <OptionCard
            key={option.value}
            selected={selectedOption === option.value}
            onClick={() => setSelectedOption(option.value)}
          >
            <OptionContent>
              <RadioCircle checked={selectedOption === option.value} />
              <OptionDetails>
                <OptionHeader>
                  <OptionLabel className="body-medium">{option.label}</OptionLabel>
                  {option.isRecommended && <Badge className="body-small">Recommended</Badge>}
                </OptionHeader>
                <FeaturesList>
                  {option.features.map((feature, index) => (
                    <FeatureItem key={index}>
                      {feature.icon}
                      <span className="body-small">{feature.text}</span>
                    </FeatureItem>
                  ))}
                </FeaturesList>
                <InfoBox variant={option.infoVariant}>
                  <img src={IMAGES.infoIcon} alt="Info" style={{width: 16, height: 16}} />
                  <p className="body-small">{option.infoMessage}</p>
                </InfoBox>
              </OptionDetails>
              {option.icon}
            </OptionContent>
          </OptionCard>
        ))}
      </OptionsList>
      <StyledActionButton
        buttonText="Continue"
        callback={!selectedOption ? null : handleSubmit}
        isLoading={isLoading}
        isDisabled={!selectedOption || isLoading}
        rightIcon={<img src={IMAGES.arrowRight} alt="Continue" style={{width: 20, height: 20}} />}
      />
    </>
  );
};

const AllFiIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  width: 100%;
  max-width: 600px;
  text-align: center;

  & > p {
    color: #4b5563;
    line-height: 1.5;
    margin: 0;
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
  width: 100%;
  max-width: 600px;
`;

const OptionCard = styled.div`
  position: relative;
  padding: 1.25rem;
  border-radius: 12px;
  border: 2px solid ${(props) => (props.selected ? '#9333ea' : '#e2e8f0')};
  background-color: ${(props) => (props.selected ? '#f5f3ff' : '#ffffff')};
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    border-color: ${(props) => (props.selected ? '#9333ea' : '#cbd5e1')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const OptionContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const RadioCircle = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.checked ? '#9333ea' : '#cbd5e1')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.125rem;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 50%;
    background-color: ${(props) => (props.checked ? '#9333ea' : 'transparent')};
  }
`;

const OptionDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const OptionLabel = styled.label`
  color: #000000;
  font-weight: 600;
  cursor: pointer;
`;

const Badge = styled.span`
  background-color: #dcfce7;
  color: #166534;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  & > span {
    color: #4b5563;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const InfoBox = styled.div`
  background-color: ${(props) => (props.variant === 'warning' ? '#fef3c7' : '#dbeafe')};
  padding: 0.75rem;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  & > p {
    color: ${(props) => (props.variant === 'warning' ? '#92400e' : '#1e40af')};
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const StyledActionButton = styled(ActionButton)`
  width: 100%;
  max-width: 600px;
  margin-top: 2rem;
`;

InitialChoiceView.propTypes = propTypes;

export default InitialChoiceView;
